/**
 * JobMockAI — InterviewReadinessScore (Phase 4 Step 8)
 *
 * Large circular gauge (0-100) with animated fill, color coding
 * (red < 40, yellow 40-70, green > 70), and component breakdown.
 *
 * variant:
 *  - card: full dashboard / progress page panel
 *  - rail: compact panel for the ~300px right rail
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

const scoreColor = (score) => {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
};

const componentLabels = {
  score_quality: 'Score Quality',
  practice_volume: 'Practice Volume',
  consistency: 'Consistency',
  skill_scores: 'Skill Scores',
  improvement_trend: 'Improvement',
  volume: 'Practice Volume',
  recency: 'Recency',
  system_design: 'System Design',
  behavioral: 'Behavioral',
  communication: 'Communication',
  problem_solving: 'Problem Solving',
  technical: 'Technical',
  // Legacy keys (keep for backward compat with cached data)
  average_score: 'Score Quality',
  interview_volume: 'Practice Volume',
};

function ComponentBars({ components }) {
  if (!components || Object.keys(components).length === 0) return null;
  return (
    <Box data-testid="readiness-components">
      {Object.entries(components).map(([key, val]) => (
        <Box key={key} sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption">{componentLabels[key] || key}</Typography>
            <Typography variant="caption" fontWeight={600}>{Math.round(val)}</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Number(val) || 0)}
            color={scoreColor(val)}
            sx={{ height: 6, borderRadius: 3 }}
            data-testid={`component-${key}`}
          />
        </Box>
      ))}
    </Box>
  );
}

ComponentBars.propTypes = {
  components: PropTypes.object,
};

const InterviewReadinessScore = ({
  score = 0,
  components = {},
  change,
  loading,
  variant = 'card',
  onViewProgress,
}) => {
  if (loading) {
    if (variant === 'rail') {
      return (
        <Box
          data-testid="readiness-score-loading"
          data-variant="rail"
          sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Skeleton variant="circular" width={88} height={88} sx={{ mx: 'auto', mb: 1.5 }} />
          <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
          <Skeleton variant="rounded" height={6} sx={{ mt: 1 }} />
          <Skeleton variant="rounded" height={6} sx={{ mt: 1 }} />
        </Box>
      );
    }
    return (
      <Card variant="outlined" data-testid="readiness-score-loading">
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Skeleton variant="circular" width={140} height={140} sx={{ mx: 'auto', mb: 2 }} />
          <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  const normalizedScore = Math.min(100, Math.max(0, Number(score) || 0));
  const color = scoreColor(normalizedScore);

  if (variant === 'rail') {
    return (
      <Box
        data-testid="readiness-score"
        data-variant="rail"
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25, lineHeight: 1.3 }}>
          Interview Readiness
        </Typography>

        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <CircularProgress
            variant="determinate"
            value={normalizedScore}
            size={88}
            thickness={5}
            color={color}
            data-testid="readiness-gauge"
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5" fontWeight={800} color={`${color}.main`} data-testid="readiness-value" sx={{ lineHeight: 1 }}>
              {normalizedScore}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              / 100
            </Typography>
          </Box>
        </Box>

        {change !== undefined && change !== null && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1.25 }}
            data-testid="readiness-change"
          >
            {change > 0 ? <TrendingUp color="success" fontSize="small" /> :
             change < 0 ? <TrendingDown color="error" fontSize="small" /> :
             <TrendingFlat color="action" fontSize="small" />}
            <Typography variant="caption" color={change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary'}>
              {change > 0 ? '+' : ''}{change} this week
            </Typography>
          </Box>
        )}

        <ComponentBars components={components} />

        {onViewProgress && (
          <Button
            size="small"
            fullWidth
            onClick={onViewProgress}
            data-testid="rail-readiness-view-progress"
            sx={{ mt: 0.5, textTransform: 'none', fontWeight: 600 }}
          >
            View progress
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Card variant="outlined" data-testid="readiness-score">
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Interview Readiness Score
        </Typography>

        {/* Circular gauge */}
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
          <CircularProgress
            variant="determinate"
            value={normalizedScore}
            size={140}
            thickness={6}
            color={color}
            data-testid="readiness-gauge"
          />
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography variant="h3" fontWeight={700} color={`${color}.main`} data-testid="readiness-value">
              {normalizedScore}
            </Typography>
            <Typography variant="caption" color="text.secondary">/ 100</Typography>
          </Box>
        </Box>

        {/* Change indicator */}
        {change !== undefined && change !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 2 }} data-testid="readiness-change">
            {change > 0 ? <TrendingUp color="success" fontSize="small" /> :
             change < 0 ? <TrendingDown color="error" fontSize="small" /> :
             <TrendingFlat color="action" fontSize="small" />}
            <Typography variant="body2" color={change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary'}>
              {change > 0 ? '+' : ''}{change} from last week
            </Typography>
          </Box>
        )}

        <ComponentBars components={components} />
      </CardContent>
    </Card>
  );
};

InterviewReadinessScore.propTypes = {
  score: PropTypes.number,
  components: PropTypes.object,
  change: PropTypes.number,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['card', 'rail']),
  onViewProgress: PropTypes.func,
};

export default InterviewReadinessScore;
